export const themeTokenFamilies = {
    light: {
        surface: {
            canvas: "#ffffff",
            island: "#ffffff",
            elevated: "#ffffff",
            low: "#fafafa",
            input: "#f6f6fb",
            neutral: "#f1f1f5",
            media: "#ffffff",
            overlay: "rgba(18, 18, 18, 0.2)",
        },
        text: {
            primary: "#1b1b1f",
            secondary: "#6b6b76",
            muted: "#8b8992",
            onAccent: "#ffffff",
            onPrimarySoft: "#4a47b1",
            onDark: "#ffffff",
            link: "#5f63dd",
        },
        border: {
            default: "#ebebeb",
            strong: "#d0d7de",
        },
        brand: {
            title: "#190064",
            iconFilter: "none",
        },
        action: {
            primary: "#6965db",
            primaryHover: "#5753d0",
            primaryDarker: "#5b57d1",
            primaryDarkest: "#4a47b1",
            primarySoft: "#e3e2fe",
            danger: "#e03131",
            dangerSoft: "#f8e8e8",
            dangerText: "#9b1c1c",
            success: "#1fb77a",
            successSoft: "#d6ebe1",
            successText: "#17623e",
            warning: "#d4a72c",
            warningSoft: "#fff8c5",
            warningText: "#6d5509",
            infoSoft: "#eff6ff",
            infoText: "#084298",
        },
        shadow: {
            card: "0 1px 2px rgba(15, 23, 42, 0.04)",
            cardHover: "0 8px 24px rgba(15, 23, 42, 0.08)",
            modal: "0 100px 80px rgba(0, 0, 0, 0.07), 0 41.78px 33.42px rgba(0, 0, 0, 0.05), 0 22.34px 17.87px rgba(0, 0, 0, 0.042)",
        },
    },
    dark: {
        surface: {
            canvas: "#121212",
            island: "#232329",
            elevated: "hsl(245, 10%, 21%)",
            low: "hsl(240, 8%, 15%)",
            input: "#121212",
            neutral: "#363636",
            media: "#ffffff",
            overlay: "rgba(52, 58, 64, 0.12)",
        },
        text: {
            primary: "#e3e3e8",
            secondary: "#a2a0a8",
            muted: "#8b8992",
            onAccent: "#121212",
            onPrimarySoft: "#e3e3e8",
            onDark: "#ffffff",
            link: "#a8a5ff",
        },
        border: {
            default: "#343a40",
            strong: "#8e8d9c",
        },
        brand: {
            title: "#e2dfff",
            iconFilter: 'url("#exms-dark-brand-icon")',
        },
        action: {
            primary: "#a8a5ff",
            primaryHover: "#bbb8ff",
            primaryDarker: "#b2aeff",
            primaryDarkest: "#beb9ff",
            primarySoft: "#4f4d6f",
            danger: "#ffa8a5",
            dangerSoft: "#3b1f23",
            dangerText: "#ffb4ae",
            success: "#83d2a7",
            successSoft: "#d6ebe1",
            successText: "#123c29",
            warning: "#ffd66b",
            warningSoft: "#fff8c5",
            warningText: "#3f3200",
            infoSoft: "#dbeafe",
            infoText: "#0b3168",
        },
        shadow: {
            card: "0 1px 2px rgba(0, 0, 0, 0.35)",
            cardHover: "0 8px 24px rgba(0, 0, 0, 0.5)",
            modal: "0 100px 80px rgba(0, 0, 0, 0.28), 0 41.78px 33.42px rgba(0, 0, 0, 0.2), 0 22.34px 17.87px rgba(0, 0, 0, 0.16)",
        },
    },
};
export const themeCssVariables = {
    surface: {
        canvas: "--color-bg",
        island: "--island-bg-color",
        elevated: "--exms-surface-elevated",
        low: "--color-surface-low",
        input: "--input-bg-color",
        neutral: "--button-gray-1",
        media: "--exms-media-bg",
        overlay: "--exms-overlay-bg",
    },
    text: {
        primary: "--text-primary-color",
        secondary: "--color-text-secondary",
        muted: "--color-gray-60",
        onAccent: "--color-on-accent",
        onPrimarySoft: "--color-on-primary-soft",
        onDark: "--color-on-dark",
        link: "--exms-link-color",
    },
    border: {
        default: "--dialog-border-color",
        strong: "--color-border",
    },
    brand: {
        title: "--color-logo-text",
        iconFilter: "--exms-brand-icon-filter",
    },
    action: {
        primary: "--color-primary",
        primaryHover: "--color-primary-hover",
        primaryDarker: "--color-primary-darker",
        primaryDarkest: "--color-primary-darkest",
        primarySoft: "--color-primary-light",
        danger: "--color-danger",
        dangerSoft: "--exms-danger-soft",
        dangerText: "--exms-danger-text",
        success: "--exms-success",
        successSoft: "--exms-success-soft",
        successText: "--exms-success-text",
        warning: "--exms-warning",
        warningSoft: "--exms-warning-soft",
        warningText: "--exms-warning-text",
        infoSoft: "--exms-info-soft",
        infoText: "--exms-info-text",
    },
    shadow: {
        card: "--exms-shadow-card",
        cardHover: "--exms-shadow-card-hover",
        modal: "--exms-shadow-modal",
    },
};
export function getThemeCssVariables(theme) {
    const families = themeTokenFamilies[theme];
    const variables = {};
    for (const familyName of Object.keys(themeCssVariables)) {
        const names = themeCssVariables[familyName];
        const values = families[familyName];
        for (const tokenName of Object.keys(names)) {
            variables[names[tokenName]] = values[tokenName];
        }
    }
    return variables;
}
export function applyThemeTokens(theme, target) {
    target.dataset.excalidrawMsTheme = theme;
    for (const [name, value] of Object.entries(getThemeCssVariables(theme))) {
        target.style.setProperty(name, value);
    }
}
/** CSS variable names used by shared-ui gallery components. */
export const galleryTokens = {
    cardBg: "--gallery-card-bg",
    cardBorder: "--gallery-card-border",
    cardThumbnailBg: "--gallery-card-thumbnail-bg",
    cardRadius: "--gallery-card-radius",
    cardShadow: "--gallery-card-shadow",
    cardShadowHover: "--gallery-card-shadow-hover",
    tabActiveBg: "--gallery-tab-active-bg",
    tabActiveText: "--gallery-tab-active-text",
    tabInactiveText: "--gallery-tab-inactive-text",
    tabBadgeBg: "--gallery-tab-badge-bg",
    tabBadgeText: "--gallery-tab-badge-text",
    gridGap: "--gallery-grid-gap",
    titleColor: "--gallery-title-color",
    metaColor: "--gallery-meta-color",
    paginationActive: "--gallery-pagination-active",
};
export const galleryDefaults = {
    [galleryTokens.cardBg]: "var(--island-bg-color)",
    [galleryTokens.cardBorder]: "var(--color-border)",
    [galleryTokens.cardThumbnailBg]: "var(--color-surface-low)",
    [galleryTokens.cardRadius]: "var(--radius-md, 8px)",
    [galleryTokens.cardShadow]: "var(--exms-shadow-card)",
    [galleryTokens.cardShadowHover]: "var(--exms-shadow-card-hover)",
    [galleryTokens.tabActiveBg]: "var(--color-primary)",
    [galleryTokens.tabActiveText]: "var(--color-on-accent)",
    [galleryTokens.tabInactiveText]: "var(--color-text-secondary)",
    [galleryTokens.tabBadgeBg]: "var(--color-primary-light)",
    [galleryTokens.tabBadgeText]: "var(--color-on-primary-soft)",
    [galleryTokens.gridGap]: "20px",
    [galleryTokens.titleColor]: "var(--text-primary-color)",
    [galleryTokens.metaColor]: "var(--color-text-secondary)",
    [galleryTokens.paginationActive]: "var(--color-primary)",
};
export function token(name) {
    const varName = galleryTokens[name];
    const fallback = galleryDefaults[varName];
    return `var(${varName}, ${fallback})`;
}
//# sourceMappingURL=tokens.js.map